import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { UsinasContext } from "../context/UsinasContextProvider";
import { PostUsina } from "../types";
import { usinasApi } from "../../../services/api";
import SelectInput from "../../../main/components/SelectInput";
import { useNavigate } from "react-router";
import { NoChangesOnUsinaEditModal } from "./NoChangesOnUsinaEditModal";
import AddIcon from '@mui/icons-material/Add';
import BaseButton from "../../../main/components/BaseButton";
import { AddClusterFormModal } from "./AddClusterFormModal";

const schema = yup.object().shape({
  cluster: yup.string().optional().nullable(),
  newCluster: yup.string().optional().nullable(),
});

export type UsinaClusterData = yup.InferType<typeof schema>;

const UsinaClusterForm = React.forwardRef((_, ref) => {
  const { newUsina, clusters, setClusters, setNewUsina, setPVSyst } = useContext(UsinasContext);
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [openNoChangesModal, setOpenNoChangesModal] = React.useState(false);

  function updateData() {
    const clusterId = newUsina?.clusterId;
    if (clusterId) {
      setValue("cluster", clusterId);
    } else {
      setValue("cluster", undefined);
    }
  }
  useEffect(() => {
    updateData();
  }, []);

  const navigate = useNavigate();
  const onSubmit = async (data: UsinaClusterData) => {
    const clusterId = data.cluster;
    const newUsinaData: PostUsina = {
      ...newUsina,
      clusterId: clusterId ?? undefined,
      newClusterName: clusterId == "newCluster" ? data.newCluster ?? undefined : undefined,
    };
    if (!newUsinaData) return;

    const path = window.location.pathname;
    const isRegister = path.includes("cadastrar");
    if (isRegister) {
      await usinasApi.postUsina(newUsinaData);
      setNewUsina(null);
      setPVSyst(null)
      navigate("/usinas");
    }
  };

  React.useImperativeHandle(ref, () => ({
    async submitForm() {
      await handleSubmit(onSubmit)();
    },
  }));
  const clusterOptions = clusters?.map((cluster) => ({
    label: cluster.clusterName ?? "",
    value: cluster.clusterId,
  }));

  const [showNewClusterModal, setShowNewClusterModal] = React.useState(false);
  const [newClusterName, setNewClusterName] = React.useState("");

  useEffect(() => {
    if (newClusterName) {
      setValue("newCluster", newClusterName);
      setClusters([...(clusters ?? []), { clusterName: newClusterName, clusterId: "newCluster" }])
      setValue("cluster", "newCluster");
    }
  }, [newClusterName])

  return (
    <Grid
      container
      component={"form"}
      width={"100%"}
      spacing={2}
      paddingX={"10px"}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} textAlign={"start"}>
        <Typography
          variant="h6"
          sx={{
            borderBottom: "1px solid #ccc",
            paddingBottom: 1,
            marginBottom: "15px",
          }}
        >
          Cluster
        </Typography>
      </Grid>
      <Grid display={"flex"} item xs={12}>
        <SelectInput
          fieldName="cluster"
          label="Cluster"
          control={control}
          errors={errors}
          options={clusterOptions ?? []}
          sx={{ width: "100%", maxWidth: "280px", marginBottom: "30px", marginRight: "10px" }}
        />
        <Tooltip title={newClusterName ? "novo cluster já registrado" : ""}>
          <Box>
            <BaseButton
              title="Novo Cluster"
              disabled={!!newClusterName}
              startIcon={<AddIcon />}
              onClick={() => setShowNewClusterModal(true)}
              sx={{
                width: "auto",
                height: "38px",
              }}
            />
          </Box>
        </Tooltip>
      </Grid>

      <NoChangesOnUsinaEditModal
        open={openNoChangesModal}
        setOpen={setOpenNoChangesModal}
      />
      <AddClusterFormModal
        showAddFormModal={showNewClusterModal}
        setShowAddFormModal={setShowNewClusterModal}
        itemName="Cluster"
        setNewCluster={setNewClusterName} />
    </Grid>
  );
});

export default UsinaClusterForm;
